import {
  Badge,
  Button,
  buttonVariants,
  Card,
  CardContent,
  cn,
} from "@exhousing/design-system";
import { CalendarPlus, Check, Home, Inbox, Mail, Phone } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { AdviceCard } from "@/components/application/AdviceCard";
import { ComparisonTable } from "@/components/application/ComparisonTable";
import { InviteModal } from "@/components/application/InviteModal";
import { RetentionCard } from "@/components/application/RetentionCard";
import { ScoreMeter } from "@/components/application/ScoreMeter";
import { EmptyState } from "@/components/EmptyState";
import { useMe } from "@/hooks/api/useAuth";
import { isLandlord } from "@/lib/roles";
import { StatusBadge } from "@/components/StatusBadge";
import {
  useApplications,
  useCloseApplication,
} from "@/hooks/api/useApplications";

const FILTERS = [
  { key: "new", label: "New" },
  { key: "active", label: "In progress" },
  { key: "", label: "All" },
];

// Statuses that mean "the funnel is running" (everything between new and terminal).
const ACTIVE_STATUSES = new Set([
  "incomplete",
  "preselection",
  "invited",
  "viewing_planned",
  "viewing_done",
  "documents_requested",
  "documents_received",
  "screening",
  "approved",
  "reserve",
  "under_option",
  "contract_phase",
]);

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function ApplicationsPage() {
  const [filter, setFilter] = useState("new");
  const [searchParams, setSearchParams] = useSearchParams();
  const propertyId = searchParams.get("property") ?? undefined;
  const { data: applications } = useApplications({
    ...(filter === "new" ? { status: "new" } : {}),
    propertyId,
  });
  const close = useCloseApplication();
  const [showInvite, setShowInvite] = useState(false);
  const { data: me } = useMe();
  const landlordView = isLandlord(me);

  const rows = (applications ?? []).filter((a) =>
    filter === "active" ? ACTIVE_STATUSES.has(a.status) : true,
  );
  const propertyLabel =
    propertyId && rows.length > 0
      ? [rows[0].property_address, rows[0].city].filter(Boolean).join(", ")
      : null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-3xl font-medium tracking-tight">
          Applications
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Questions and rental applications from your published listings — one
          dossier per candidate per property.
        </p>
        {propertyId && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Filtered to {propertyLabel ?? "one property"} · clear
            </button>
            {!landlordView && (
              <Button size="sm" variant="outline" onClick={() => setShowInvite(true)}>
                <CalendarPlus className="size-4" />
                Viewing invitations
              </Button>
            )}
          </div>
        )}
      </header>

      {showInvite && propertyId && (
        <InviteModal propertyId={propertyId} onClose={() => setShowInvite(false)} />
      )}

      {propertyId && <AdviceCard propertyId={propertyId} readOnly={landlordView} />}

      {propertyId && <ComparisonTable applications={applications ?? []} />}

      {!propertyId && !landlordView && <RetentionCard />}

      <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={
              filter === f.key
                ? "flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                : "flex-1 rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:text-foreground"
            }
          >
            {f.label}
          </button>
        ))}
      </div>

      {rows.length > 0 ? (
        <div className="space-y-3">
          {rows.map((a) => (
            <Card key={a.id}>
              <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/applications/${a.id}`}
                      className="font-medium hover:underline"
                    >
                      {a.name}
                    </Link>
                    <StatusBadge status={a.status} />
                    {a.kind === "question" && <Badge tone="neutral">Question</Badge>}
                    {a.score != null && <ScoreMeter score={a.score} />}
                    {a.desired_start_date && (
                      <Badge tone="neutral">Move-in {a.desired_start_date}</Badge>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatDate(a.created_at)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    <a
                      href={`mailto:${a.email}`}
                      className="inline-flex items-center gap-1.5 hover:text-foreground"
                    >
                      <Mail className="size-3.5" /> {a.email}
                    </a>
                    {a.phone && (
                      <a
                        href={`tel:${a.phone}`}
                        className="inline-flex items-center gap-1.5 hover:text-foreground"
                      >
                        <Phone className="size-3.5" /> {a.phone}
                      </a>
                    )}
                    <Link
                      to={`/properties/${a.property_id}`}
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      <Home className="size-3.5" />
                      {a.property_address ?? "Property"}
                      {a.city ? `, ${a.city}` : ""}
                    </Link>
                  </div>
                  {a.message && (
                    <p className="line-clamp-3 whitespace-pre-line pt-1 text-sm">
                      {a.message}
                    </p>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Link
                    to={`/applications/${a.id}`}
                    className={cn(buttonVariants({ size: "sm", variant: "outline" }))}
                  >
                    Open dossier
                  </Link>
                  {!landlordView &&
                    (a.status === "new" || a.kind === "question") &&
                    a.status !== "cancelled" && (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={close.isPending}
                        onClick={() =>
                          close.mutate(
                            { id: a.id, reason: "Handled from the inbox" },
                            {
                              onSuccess: () => toast.success("Dossier closed"),
                              onError: () => toast.error("Could not close"),
                            },
                          )
                        }
                      >
                        <Check className="size-4" />
                        Close
                      </Button>
                    )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Inbox}
          title={filter === "new" ? "No new applications" : "No applications yet"}
          description="When someone asks a question or applies on one of your published listings, their dossier lands here."
        />
      )}
    </div>
  );
}
